/**
 *
 * Save button in bottom
 *
 */
import React from 'react';
import ReactFormContext from './ReactFormContext';
import ReactFormStateContext from './ReactFormStateContext';
import {Keyboard} from '@applane/react-core-components';
import {loadData} from '@applane/data-update-v1';
import {mergeFieldValidationState, validate} from './Validations';
import {compute} from './Computations';
import {
  modifyUpdates,
  denormalizeUpdates,
  populateNestedDataAndUpdates,
} from './ComputationResolver';
import {getNewId, getUid, putDottedValue} from './Utility';

class ReactForm extends React.PureComponent {
  state = {};

  constructor(props) {
    super(props);
    if (!props.useState || !props.state) {
      this.state = {uid: getUid(), data: {}};
    }
    let {user, getUser, navigation, eventDispatcher} = props;
    this.formContext = {
      user,
      getUser,
      navigation,
      eventDispatcher,
      setValue: this.setValue,
      setFocus: this.setFocus,
      setState: this._setState,
      handleSubmit: this.handleSubmit,
    };
  }

  getFormUpdates = () => {
    let state = this._getState() || {};
    return {
      uid: state.uid,
      data: state.data,
      updates: state.updates,
    };
  };

  addFormUpdateListener = () => {
    let {eventDispatcher, formUpdateEvent} = this.props;
    if (!formUpdateEvent || this.formUpdateEvent) {
      return;
    }
    if (formUpdateEvent === true) {
      formUpdateEvent = 'formUpdates';
    }
    eventDispatcher &&
      eventDispatcher.listen(formUpdateEvent, this.getFormUpdates);
    this.formUpdateEvent = formUpdateEvent;
  };

  componentDidMount() {
    this.loadData(this._getState());
    const uid = this.getUid();
    let {
      eventDispatcher,
      navigation,
      reloadOnChangeEvent,
      screenName,
    } = this.props;
    reloadOnChangeEvent &&
      eventDispatcher &&
      eventDispatcher.listen(reloadOnChangeEvent, this.reloadData);
    uid &&
      eventDispatcher &&
      eventDispatcher.listen(`${uid}-submit`, this.handleSubmit);
    eventDispatcher && eventDispatcher.notify(`${screenName}_formUid`, uid);
    if (navigation && navigation.state) {
      this.oldParams = navigation.state.params;
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    const uid = this.getUid();
    let {eventDispatcher, reloadOnChangeEvent} = this.props;
    reloadOnChangeEvent &&
      eventDispatcher &&
      eventDispatcher.unlisten(reloadOnChangeEvent, this.reloadData);
    uid &&
      eventDispatcher &&
      eventDispatcher.unlisten(`${uid}-submit`, this.handleSubmit);
    this.formUpdateEvent &&
      eventDispatcher &&
      eventDispatcher.unlisten(this.formUpdateEvent, this.getFormUpdates);
  }

  componentDidUpdate() {
    let {navigation, reloadOnNavigationChange} = this.props;
    if (navigation && navigation.state) {
      let {params} = navigation.state;
      if (
        reloadOnNavigationChange &&
        this.oldParams &&
        this.oldParams !== params
      ) {
        this.formContext = {
          ...this.formContext,
          navigation,
        };
        //load Data again
        let newState = {
          data: {},
          updates: void 0,
          mandatoryErrors: void 0,
          validationErrors: void 0,
          submitTried: false,
        };
        this.loadData(newState, {source: 'navigationChange'});
        this.oldParams = params;
      }
    }
  }

  getUid = () => {
    let state = this._getState();
    return state && state.uid;
  };

  _getState = () => {
    if (this.props.useState && this.props.state) {
      return this.props.state;
    } else {
      return this.state;
    }
  };

  _setState = _state => {
    if (this._unmounted) {
      return;
    }
    if (this.props.useState && this.props.setState) {
      this.props.setState(_state);
    } else {
      this.setState(_state);
    }
  };

  reloadData = () => {
    this.loadData(this._getState(), {source: 'reloadEvent'});
  };

  loadData = async (state = {}, fetchProps) => {
    let {
      fetch,
      beforeFetch,
      afterFetch,
      getUser,
      dataMode,
      uri,
      data,
      navigation,
      defaultValues,
      eventDispatcher,
    } = this.props;
    if (dataMode !== 'insert' && uri) {
      if (typeof uri === 'function') {
        uri = uri({
          user: getUser && getUser(),
          navigation,
        });
      }
      let resp = loadData({
        uri,
        fetch,
        fetchProps,
        beforeFetch,
        afterFetch,
        state,
      });
      let {beforeState, afterState} = resp;
      state = {...state, ...beforeState};
      this._setState(state);
      if (afterState) {
        afterState().then(_afterState => {
          this._setState(_afterState);
          const {data} = _afterState || {};
          eventDispatcher &&
            eventDispatcher.notify &&
            eventDispatcher.notify(`${this.props.screenName}_loadData`, data);
        });
      }
    } else {
      if (typeof data === 'function') {
        data = data({navigation});
      }
      state = {...state, data: data || {}};

      if (navigation && navigation.getParam) {
        let prevData = navigation.getParam('nextData');
        let prevUpdates = navigation.getParam('nextUpdates');
        if (prevData) {
          state.data = {...state.data, ...prevData};
        }
        if (prevUpdates) {
          state.updates = {...state.updates, ...prevUpdates};
        }
      }
      if (defaultValues && typeof defaultValues === 'function') {
        defaultValues = defaultValues({navigation});
      }
      let values = defaultValues;
      if (!state.data._id) {
        values = values || {};
        values._id = values._id || getNewId();
        state = {...state, data: {...data, _id: values._id}};
      }
      if (values && Object.keys(values).length) {
        this.handleUpdates(state, {values});
      } else {
        this._setState(state);
      }
    }
  };

  handleSubmit = async params => {
    try {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      Keyboard.dismiss();
      let {
        mandatoryErrors,
        hasMandatoryError,
        validationErrors,
        hasValidationError,
      } = await this.validateState({state: this._getState()});
      if (hasMandatoryError || hasValidationError) {
        this.submitting = false;
        this._setState({
          mandatoryErrors,
          validationErrors,
          submitTried: true,
        });
        this.props.onValidationError &&
          this.props.onValidationError({mandatoryErrors, validationErrors});
        return;
      }

      let {
        navigation,
        eventDispatcher,
        dataMode,
        onSubmit,
        passDataToNext,
        closeView,
        reloadEvent,
        submitNext,
        next,
        onSubmitSuccess,
        onSubmitError,
        beforeSubmit,
        afterSubmit,
        skipNoChangeError
      } = this.props;
      let {data, updates} = this._getState();
      if (next) {
        let nextParams = void 0;
        if (passDataToNext) {
          if (typeof passDataToNext === 'function') {
            nextParams = passDataToNext(this._getState(), {navigation});
          } else {
            nextParams = {
              nextData: {...data},
              nextUpdates: {...updates},
            };
          }
        }
        this.submitting = false;
        navigation &&
          navigation.navigate &&
          navigation.navigate(next, nextParams);
      } else {
        this._setState({submitTried: true, submitting: true});
        let beforeSubmitState = {};
        if (beforeSubmit) {
          let modifiedState = await beforeSubmit({
            ...params,
            navigation,
            eventDispatcher,
            data,
            updates,
          });
          if (modifiedState && typeof modifiedState === 'object') {
            beforeSubmitState = modifiedState;
            let {data: modifiedData, updates: modifiedUpdates} = modifiedState;
            if (modifiedData) {
              data = modifiedData;
            }
            if (modifiedUpdates) {
              updates = modifiedUpdates;
            }
          }
        }
        if (!skipNoChangeError && (!updates || !Object.keys(updates).length))  {
          this.submitting = false;
          this._setState({submitting: false});
          let noChangesError = new Error('No Changes Found');
          noChangesError.code = 'no_changes_found';
          onSubmitError && onSubmitError(noChangesError, this._setState);
          return;
        }
        if (beforeSubmitState && Object.keys(beforeSubmitState).length) {
          this._setState(beforeSubmitState);
        }
        let result =
          onSubmit &&
          (await onSubmit({
          ...params,
          navigation,
          eventDispatcher,
          dataMode,
          data,
          updates,
        }));
        let afterSubmitParams = {
          ...params,
          navigation,
          eventDispatcher,
          submitResult: result,
          data,
        };
        let afterSubmitState = {submitTried: false, submitting: false};
        if (afterSubmit) {
          let modifiedState = await afterSubmit(afterSubmitParams);
          if (modifiedState && typeof modifiedState === 'object') {
            afterSubmitState = {
              ...afterSubmitState,
              ...modifiedState,
            };
          }
        }
        this.submitting = false;
        onSubmitSuccess && onSubmitSuccess(this._setState);
        this._setState(afterSubmitState);
        submitNext && submitNext(afterSubmitParams);
        if (reloadEvent && eventDispatcher && eventDispatcher.notify) {
          if (Array.isArray(reloadEvent)) {
            reloadEvent.forEach(_event =>
              eventDispatcher.notify(_event, result),
            );
          } else {
            eventDispatcher.notify(reloadEvent, result);
          }
        }
        if (typeof closeView === 'function') {
          closeView = closeView({navigation, eventDispatcher});
        }
        if (closeView) {
          navigation && navigation.pop && navigation.pop(closeView);
          return;
        }
      }
    } catch (err) {
      this.submitting = false;
      this.props.onSubmitError && this.props.onSubmitError(err, this._setState);
      this._setState({submitting: false});
    }
  };

  validateOnBlur = ({path, field}) => {
    setTimeout(async _ => {
      let revisedState = await this.validateState({
        state: this._getState(),
        field,
        path,
      });
      // for merging validation state by field,
      // because on change of other field validation it does not affect current state validations
      // Vishal Joshi - 26 June 2021
      revisedState = mergeFieldValidationState({
        state: this._getState(),
        newState: revisedState,
        field,
        path,
      });
      this._setState({...revisedState});
    }, 0);
  };

  setFocus = focusProps => {
    let {setFocus} = this.props;
    let state = this._getState();
    let {data, focusField, focussedFields} = state;
    if (setFocus) {
      //case of nested table field focus
      setFocus({data, ...focusProps});
      return;
    }
    let {field, path, focus} = focusProps;
    if (!field) {
      return;
    }
    if (path && path.length) {
      let {_id: nestedId, field: nestedField} = path[0];
      if (path.length > 1) {
        let {_id: innerNestedId, field: innerNestedField} = path[1];
        if (focus) {
          focusField = {
            [nestedField]: {
              [nestedId]: {
                [innerNestedField]: {
                  [innerNestedId]: field,
                },
              },
            },
          };
        } else {
          let nestedFocusField =
            focusField &&
            focusField[nestedField] &&
            focusField[nestedField][nestedId];
          let innerNestedFocusField =
            nestedFocusField &&
            nestedFocusField[innerNestedField] &&
            nestedFocusField[innerNestedField][innerNestedId];
          if (innerNestedFocusField === field) {
            focusField = void 0;
          }
          let nestedFocussedFields = {
            ...(focussedFields ? focussedFields[nestedField] : void 0),
          };
          let innerNestedFocussedFields = {
            ...(nestedFocussedFields &&
              nestedFocussedFields[nestedId] &&
              nestedFocussedFields[nestedId][innerNestedField]),
          };
          focussedFields = {
            ...focussedFields,
            [nestedField]: {
              ...nestedFocussedFields,
              [nestedId]: {
                ...nestedFocussedFields[nestedId],
                [innerNestedField]: {
                  ...innerNestedFocussedFields,
                  [innerNestedId]: {
                    ...innerNestedFocussedFields[innerNestedId],
                    [field]: 1,
                  },
                },
              },
            },
          };
        }
      } else {
        if (focus) {
          focusField = {
            [nestedField]: {
              [nestedId]: field,
            },
          };
        } else {
          let nestedFocusField =
            focusField &&
            focusField[nestedField] &&
            focusField[nestedField][nestedId];
          if (nestedFocusField === field) {
            focusField = void 0;
          }
          let nestedFocussedFields = {
            ...(focussedFields ? focussedFields[nestedField] : void 0),
          };
          focussedFields = {
            ...focussedFields,
            [nestedField]: {
              ...nestedFocussedFields,
              [nestedId]: {
                ...nestedFocussedFields[nestedId],
                [field]: 1,
              },
            },
          };
        }
      }
    } else {
      if (focus) {
        focusField = field;
      } else {
        if (focusField === field) {
          focusField = void 0;
        }
        focussedFields = {...focussedFields, [field]: 1};
      }
    }
    if (!focus && this.props.validateOnBlur) {
      this.validateOnBlur({path, field});
    }
    this._setState({
      focusField,
      focussedFields,
    });
  };

  handleAutoSave = async ({data, field, value}) => {
    let {onSubmit} = this.props;
    if (!onSubmit) {
      return;
    }
    try {
      let result = await onSubmit({
        data,
        updates: {[field]: value},
      });
      return result;
    } catch (err) {
      console.error('Error in DateUpdate autosave in data update', err);
      //ToDo handle error
    }
  };

  resolveComputations = async (state, newState, updates) => {
    let {computations, computeProps} = this.props;
    if (computations && (computations.self || computations.children)) {
      let computedUpdates = await compute({
        computations,
        computeProps,
        data: newState.data,
        updates: updates,
      });
      if (computedUpdates) {
        newState = await this.modifyDataAndUpdates(
          {...state, ...newState},
          computedUpdates,
        );
      }
    }
    return newState;
  };

  modifyDataAndUpdates = async (state, updates) => {
    let {nestedFields} = this.props;
    let modifiedUpdates = denormalizeUpdates({
      updates,
      data: state.data,
      children: nestedFields,
    });
    if (!modifiedUpdates || !modifiedUpdates.length) {
      return;
    }
    let newState = {data: {...state.data}, updates: {...state.updates}};
    for (let modifiedUpdate of modifiedUpdates) {
      let {path, updates: updatesToSet} = modifiedUpdate;
      let {set, ...restUpdates} = updatesToSet;
      let newData = set;
      let newUpdates = set;
      if (path && path.length) {
        let {nestedData, nestedUpdates} = populateNestedDataAndUpdates(
          newState,
          {
            path,
            values: {...set},
            ...restUpdates,
          },
        );
        newData = nestedData;
        newUpdates = nestedUpdates;
      }
      for (let key in newData) {
        putDottedValue(newState.data, key, newData[key]);
        if (this.props.validateOnChange && !this.props.validateAllOnChange) {
          newState = await this.validateState({
            state,
            newState,
            field: key,
            path,
          });
        }
      }
      for (let key in newUpdates) {
        putDottedValue(newState.updates, key, newUpdates[key]);
      }
    }
    if (this.props.validateAllOnChange) {
      newState = await this.validateState({
        state,
        newState,
      });
    }
    this._setState(newState);
    return await this.resolveComputations(state, newState, modifiedUpdates);
  };

  handleUpdates = (state, valueProps) => {
    let modifiedUpdates = modifyUpdates(state, valueProps);
    if (!modifiedUpdates) {
      return;
    }
    this.addFormUpdateListener();
    this.modifyDataAndUpdates(state, modifiedUpdates);
  };

  setValue = setValueProps => {
    let state = this._getState();
    let {data} = state;
    let {setValue, saveOnChange} = this.props;
    if (setValue) {
      //case of nested table field updates
      setValue({data, ...setValueProps});
      return;
    }
    const {field, value, path} = setValueProps;
    if (data && !path && saveOnChange) {
      this.handleAutoSave({data, field, value});
    }
    this.handleUpdates(state, setValueProps);
  };

  validateState = async ({state, newState, field, path}) => {
    let {mandatory, validations, validateOnUpdate, mandatoryMessage, nestedFields, checkCurrentValidations} = this.props;
    let revisedState = await validate(
      {...state, ...newState},
      {
        field,
        mandatory,
        validations,
        validateOnUpdate,
        mandatoryMessage,
        nestedFields,
        path,
        checkCurrentValidations
      },
    );
    // for merging validation state by field,
    // because on change of other field validation it does not affect current state validations
    // Vishal Joshi - 26 June 2021
    // revisedState = mergeFieldValidationState({
    //   state: this._getState(),
    //   newState: revisedState,
    //   field,
    //   path,
    // });
    // return {
    //   ...state,
    //   ...revisedState,
    // };
    return revisedState;
  };

  render() {
    let state = this._getState();
    let {children} = this.props;
    return (
      <ReactFormContext.Provider value={this.formContext}>
        <ReactFormStateContext.Provider value={state}>
          {typeof children === 'function'
            ? children({
                form_context: this.formContext,
                form_state: state,
              })
            : children}
        </ReactFormStateContext.Provider>
      </ReactFormContext.Provider>
    );
  }
}
export default ReactForm;
